import { autorun, observable } from "mobx";
import {
  AttributeTypes,
  AttributeValueTypes,
  ItemReOrderIntervals,
} from "../enums";
import {
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  IColor,
  InputErrorModel,
  nameof,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { PriceModel } from "./Price.model";
import { ItemOptionsModel } from "./ItemOptions.model";
import { MediaModel } from "./MediaModel";
import { IAPIInventorySettings } from "../interfaces/IAPIDonationAndItemModel";
import { IItemDetails } from "../interfaces/IItemDetails";
import {
  IAPIProductSelectedAttributesModel,
  IAPIProductSelectedOptionsModel,
  IAPIReOrderSettings,
} from "../interfaces/IAPIProductModels";
import {
  IAPIProductAttributeOptionsMappingModel,
  IAPIProductVariantInputsOptionsModel,
  IAPIProductVariantsModel,
} from "../interfaces/IAPIProductVariantModel";

export class ColorModel {
  hex: string;
  isGradient: boolean;
  gradient: string;
  rgbValue?: RGBA;
  rgba: string;
  constructor(data?: Partial<ColorModel>) {
    this.hex = data?.hex || "";
    this.isGradient = data?.isGradient || false;
    this.gradient = data?.gradient || "";
    this.rgbValue = data?.rgbValue || { r: "", g: "", b: "", a: "" };
    this.rgba = data?.rgba || "";
  }

  static deserializeAPIModel(data: IColor): ColorModel {
    const mappedData: Partial<ColorModel> = {
      hex: data.hex,
      isGradient: data.isGradient,
      gradient: data.gradient,
      rgbValue: data.rgbValue,
      rgba: data.rgba,
    };
    return new ColorModel(mappedData);
  }

  static createAPIModel(data: ColorModel): IColor {
    return {
      hex: data.hex,
      isGradient: data.isGradient,
      gradient: data.gradient,
      rgbValue: data.rgbValue,
      rgba: data.rgba,
    } as IColor;
  }
}

export class RGBA {
  r: string;
  g: string;
  b: string;
  a: string;
  constructor(data?: Partial<RGBA>) {
    this.r = data?.r || "";
    this.g = data?.g || "";
    this.b = data?.b || "";
    this.a = data?.a || "";
  }
}
export class ProductModel extends CoreModel implements IItemDetails {
  name: string;
  description: string;
  descriptionRawEditorContent: string;
  category: string;
  medias: string[];
  hasVariants: boolean;
  tax: boolean;
  surcharge: boolean;
  discount: boolean;
  review: boolean;
  return: boolean;
  sku: string;
  barCode: string;
  returnDays: number;
  allowReOrder: boolean;
  reOrderSttings?: ReOrderSttingsRequest | null;
  priceCreateData?: PriceModel[];
  inventorySettings?: InventorySettings | null;
  options?: ItemOptionsModel | null;
  defaultPriceId?: string | null;
  priceIds?: string | [];
  constructor(data?: Partial<ProductModel>) {
    super();
    this.name = (data && data.name) || "";
    this.description = (data && data.description) || "";
    this.descriptionRawEditorContent =
      (data && data.descriptionRawEditorContent) || "";
    this.category = (data && data.category) || "";
    this.medias = (data && data.medias) || [];
    this.hasVariants = data?.hasVariants === true;
    this.tax = data?.tax === true;
    this.surcharge = data?.surcharge === true;
    this.discount = data?.discount === true;
    this.review = data?.review === true;
    this.hasVariants = data?.hasVariants === true;
    this.return = data?.return === true;
    this.sku = (data && data.sku) || "";
    this.barCode = (data && data.barCode) || "";
    this.returnDays = (data && data.returnDays) || 0;
    this.allowReOrder = data?.allowReOrder === true;
    this.reOrderSttings = data?.reOrderSttings || null;
    this.priceCreateData = data?.priceCreateData || [];
    this.options = data?.options || null;
    this.defaultPriceId = (data && data.defaultPriceId) || "";
    this.priceIds = (data && data.priceIds) || [];
  }
}

export class ReOrderSttingsRequest {
  reOrderIntervals: (keyof typeof ItemReOrderIntervals)[];
  discountPercentage: number;
  constructor(data?: Partial<ReOrderSttingsRequest>) {
    this.reOrderIntervals = data?.reOrderIntervals || [];
    this.discountPercentage = (data && data.discountPercentage) || 0;
  }

  static deserializeAPIModel(data: IAPIReOrderSettings): ReOrderSttingsRequest {
    const mappedData: Partial<ReOrderSttingsRequest> = {
      reOrderIntervals: data.reOrderIntervals,
      discountPercentage: data.discountPercentage,
    };
    return new ReOrderSttingsRequest(mappedData);
  }
}

export class InventorySettings {
  available: number;
  isSellOnOutOfStock: boolean;
  isPromote: boolean;
  isLimitPurchaseQuantity: boolean;
  quantityLimit: number;
  isInStock: boolean;
  hideAvailable: boolean;
  constructor(data?: Partial<InventorySettings>) {
    this.available = (data && data.available) || 0;
    this.isSellOnOutOfStock = data?.isSellOnOutOfStock === true;
    this.isPromote = data?.isPromote === true;
    this.isLimitPurchaseQuantity = data?.isLimitPurchaseQuantity === true;
    this.quantityLimit = (data && data.quantityLimit) || 0;
    this.isInStock = data?.isInStock === true;
    this.hideAvailable = data?.hideAvailable === true;
  }
  static deserializeAPIModel(
    data: IAPIInventorySettings,
    hasVariants: boolean
  ): InventorySettings {
    const mappedData: Partial<InventorySettings> = {
      available: data.available,
      isSellOnOutOfStock: data.isSellOnOutOfStock,
      isPromote: data.isPromote,
      isLimitPurchaseQuantity: data.isLimitPurchaseQuantity,
      quantityLimit: data.quantityLimit,
      isInStock: data.isInStock,
      hideAvailable: hasVariants,
    };
    return new InventorySettings(mappedData);
  }
}

export class ProductAttributesModel {
  id: string;
  attributeType: AttributeTypes | "";
  valueType: AttributeValueTypes;
  name: string | "";
  options: ProductAttributeOptionsModel[];
  errors: InputErrorModel<ProductAttributesModel>[];
  constructor(data?: Partial<ProductAttributesModel>) {
    this.id = data?.id || "";
    this.name = data?.name || "";
    this.options =
      data?.options?.map((opt) => {
        return new ProductAttributeOptionsModel(opt);
      }) || [];
    this.attributeType = data?.attributeType || "";
    this.valueType = data?.valueType || AttributeValueTypes.Text;
    this.errors = data?.errors || [];

    if (!this.name) {
      this.attributeType = AttributeTypes.Custom;
    }
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof ProductAttributesModel): boolean {
    return this.errors.some((e) => e.field === key);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductAttributesModel>[] = [];
    if (!this.name?.trim()) {
      newErrors.push({
        field: nameof<ProductAttributesModel>("name"),
      });
    }
    this.options.forEach((element) => {
      element.validate(isAutoRun);
    });

    //DO NOT ADD ANY NEW ERROR
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }

    const hasOptionsError = this.options.findIndex((x) => x.hasError) > -1;
    return this.errors.length === 0 && !hasOptionsError;
  }

  static deserializeAPIModel(
    data: IAPIProductSelectedAttributesModel
  ): ProductAttributesModel {
    const mappedData: Partial<ProductAttributesModel> = {
      id: data.attributeId,
      valueType: data.valueType,
      attributeType: data.type,
      name: data.attributeName,
      options:
        data?.options?.map((o) => {
          return ProductAttributeOptionsModel.deserializeAPIModel(o);
        }) || [],
    };
    return new ProductAttributesModel(mappedData);
  }
}

export class ProductAttributeOptionsModel {
  @observable name: string;
  color?: ColorModel | null;
  showColorPicker: boolean;
  hasError: boolean;
  id: string;
  referenceId: string;
  constructor(data?: Partial<ProductAttributeOptionsModel>) {
    this.name = data?.name || "";
    this.color = (data?.color && new ColorModel(data.color)) || null;
    this.showColorPicker = data?.showColorPicker === true;
    this.hasError = data?.hasError === true;
    this.id = data?.id || "";

    if (!data?.referenceId) {
      this.referenceId = UtilityMethods.uniqueId(10);
    } else {
      this.referenceId = data?.referenceId || "";
    }

    autorun(() => this.validate(true));
  }

  validate(isAutoRun = false) {
    if (!this.name?.trim()) {
      if (!isAutoRun) {
        this.hasError = true;
      }
    } else {
      this.hasError = false;
    }
  }

  static deserializeAPIModel(
    data: IAPIProductSelectedOptionsModel
  ): ProductAttributeOptionsModel {
    const mappedData: Partial<ProductAttributeOptionsModel> = {
      name: data.optionName,
      id: data.optionId,
      color: data.color
        ? ColorModel.deserializeAPIModel(data.color)
        : undefined,
      referenceId: new Date().getTime().toString(),
    };
    return new ProductAttributeOptionsModel(mappedData);
  }

  deserializeColor(): IColor {
    return this.color as IColor;
  }
}

export class ProductAttributeInputModel {
  @observable priceByVariant: boolean;
  @observable originalPriceByVariant: boolean;
  @observable costByVariant: boolean;
  @observable showOnHand: boolean;
  options: ProductAttributeMappingsInput[];
  errors: InputErrorModel<ProductAttributeInputModel>[];

  constructor(data?: Partial<ProductAttributeInputModel>) {
    this.priceByVariant = data?.priceByVariant === true;
    this.originalPriceByVariant = data?.originalPriceByVariant === true;
    this.costByVariant = data?.costByVariant === true;
    this.options =
      data?.options?.map((d) => new ProductAttributeMappingsInput(d)) || [];
    this.errors = data?.errors || [];
    this.showOnHand = data?.showOnHand === true;
    autorun(() => {
      this.options.forEach((x) => {
        x.price = !this.priceByVariant ? "" : x.price;
        x.originalPrice = !this.originalPriceByVariant ? "" : x.originalPrice;
        x.cost = !this.costByVariant ? "" : x.cost;
      });
      this.validate(true);
    });
  }

  allowedOptions(variantIds: string[]): ProductAttributeMappingsInput[] {
    return this.options.filter((x) => variantIds?.includes(x.id));
  }

  getVariantIndex(
    isTrackInventory: boolean,
    selectedAttributeId: string,
    selectdOptionId: string,
    onlyAllowedVariantIds?: string[]
  ): number {
    let allowedOptions = this.options;

    if (onlyAllowedVariantIds && onlyAllowedVariantIds?.length > 0) {
      allowedOptions = this.options.filter((x) =>
        onlyAllowedVariantIds?.includes(x.id)
      );
    }

    const selectedVariant = allowedOptions.find(
      (x) =>
        (!isTrackInventory ||
          (isTrackInventory && x.onHand && parseFloat(x.onHand) > 0)) &&
        x.attributeMappings.find(
          (y) =>
            y.attributeId === selectedAttributeId &&
            y.optionId === selectdOptionId
        )
    );
    if (!selectedVariant) return -1;

    let newVariantIndex = this.options.findIndex((x) =>
      x.attributeMappings.every((y) =>
        selectedVariant.attributeMappings.find(
          (z) => z.attributeId === y.attributeId && z.optionId === y.optionId
        )
      )
    );

    return newVariantIndex;
    //allowedOptions
  }

  // private validateDecimal(value: string, index: number, key: keyof ProductAttributeMappingsInput, errors: any[]) {
  //     if (!value || !DecimalRegex.test(value)) {
  //         errors.push({
  //             field: nameof<ProductAttributeMappingsInput>(key),
  //             index: index,
  //         });
  //     } else if (parseFloat(value) <= 0) {
  //         errors.push({
  //             field: nameof<ProductAttributeMappingsInput>(key),
  //             index: index,
  //         });
  //     }
  // }

  validate(isAutoRun = false): any[] {
    const newErrors: any[] = [];
    this.options?.forEach((x, idx) => {
      if (!x.isRemoved) {
        this.priceByVariant &&
          CoreInputModelUtil.validateDecimalValue(
            x.price,
            nameof<ProductAttributeMappingsInput>("price"),
            newErrors,
            idx,
            true
          );
        this.originalPriceByVariant &&
          CoreInputModelUtil.validateDecimalValue(
            x.originalPrice,
            nameof<ProductAttributeMappingsInput>("originalPrice"),
            newErrors,
            idx,
            true
          );
        this.costByVariant &&
          CoreInputModelUtil.validateDecimalValue(
            x.cost,
            nameof<ProductAttributeMappingsInput>("cost"),
            newErrors,
            idx,
            true
          );
        // CoreInputModelUtil.validateDecimalValue(
        //   x.available,
        //   nameof<ProductAttributeMappingsInput>("available"),
        //   newErrors,
        //   idx,
        //   false
        // );
        this.showOnHand &&
          CoreInputModelUtil.validateDecimalValue(
            x.onHand,
            nameof<ProductAttributeMappingsInput>("onHand"),
            newErrors,
            idx,
            false
          );
      }
      // this.validateText(x.sku, idx, nameof<ProductAttributeMappingsInput>('sku'), newErrors);
      // this.validateText(x.barCode, idx, nameof<ProductAttributeMappingsInput>('barCode'), newErrors);
      //
    });

    //DO NOT ADD ANY NEW ERROR
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors;
  }

  createAPIModel(isTrackInventory: boolean = true): IAPIProductVariantsModel {
    const request = {
      priceByVariant: this.priceByVariant,
      originalPriceByVariant: this.originalPriceByVariant,
      costByVariant: this.costByVariant,
      options: this.options.map((d) => {
        return d.createAPIModel(isTrackInventory);
      }),
    };
    return request as IAPIProductVariantsModel;
  }

  static deserializeAPI(
    data: IAPIProductVariantsModel,
    isTrackInventory: boolean
  ): ProductAttributeInputModel {
    const mappedData: Partial<ProductAttributeInputModel> = {
      priceByVariant: data.priceByVariant,
      originalPriceByVariant: data.originalPriceByVariant,
      costByVariant: data.costByVariant,
      options:
        data.options?.map((o) => {
          return ProductAttributeMappingsInput.deserializeAPIModel(o);
        }) || [],
      showOnHand: isTrackInventory,
    };
    return new ProductAttributeInputModel(mappedData);
  }

  static deserializeAPIWithOptions(
    data: IAPIProductVariantsModel,
    isTrackInventory: boolean,
    selectedVariantIds: string[]
  ): ProductAttributeInputModel {
    const options =
      selectedVariantIds &&
      data.options.filter((option) => selectedVariantIds.includes(option.id));
    const mappedData: Partial<ProductAttributeInputModel> = {
      priceByVariant: data.priceByVariant,
      originalPriceByVariant: data.originalPriceByVariant,
      costByVariant: data.costByVariant,
      options:
        options?.map((o) => {
          return ProductAttributeMappingsInput.deserializeAPIModel(o);
        }) || [],
      showOnHand: isTrackInventory,
    };
    return new ProductAttributeInputModel(mappedData);
  }
}

export class ProductAttributeMappingsInput {
  //id coming from api
  id: string;
  sku: string;
  barCode: string;
  price: string;
  originalPrice: string;
  cost: string;
  attributeMappings: ProductAttributeWithOption[];
  isRemoved: boolean;
  onHand: string;
  name: string;

  //Just for comparison purpose
  referenceIds: string;
  constructor(data?: Partial<ProductAttributeMappingsInput>) {
    this.id = data?.id || "";
    this.sku = (data && data.sku) || "";
    this.barCode = (data && data.barCode) || "";
    this.price = (data && data.price) || "";
    this.originalPrice = (data && data.originalPrice) || "";
    this.cost = (data && data.cost) || "";
    this.attributeMappings =
      data?.attributeMappings?.map((x) => {
        return new ProductAttributeWithOption(x);
      }) || [];
    this.isRemoved = data?.isRemoved === true;
    this.onHand = data?.onHand || "";

    const names = this.attributeMappings.map((a) => {
      return a.optionName;
    });
    this.name = names.join(" / ");

    this.referenceIds = this.attributeMappings
      .map((a) => a.referenceId)
      .join(",");
  }

  get isAvailableBasedOnOnHand(): boolean {
    return this.onHand ? parseFloat(this.onHand) > 0 : false;
  }

  createAPIModel(
    isTrackInventory: boolean = true
  ): IAPIProductVariantInputsOptionsModel {
    const request = {
      id: this.id,
      sku: this.sku,
      barCode: this.barCode,
      price: !this.price ? 0 : parseFloat(this.price),
      originalPrice: !this.originalPrice ? 0 : parseFloat(this.originalPrice),
      cost: !this.cost ? 0 : parseFloat(this.cost),
      attributeMappings: this.attributeMappings.map((d) => {
        return d.createAPIModel();
      }),
      isRemoved: this.isRemoved,
      onHandInventory:
        isTrackInventory && this.onHand ? parseFloat(this.onHand) : 0,
    };
    return request as IAPIProductVariantInputsOptionsModel;
  }

  static deserializeAPIModel(
    data: IAPIProductVariantInputsOptionsModel
  ): ProductAttributeMappingsInput {
    const mappedData: Partial<ProductAttributeMappingsInput> = {
      id: data.id,
      sku: data.isRemoved ? "" : data.sku,
      barCode: data.isRemoved ? "" : data.barCode,
      price: data.isRemoved ? "" : data.price.toString(),
      originalPrice: data.isRemoved ? "" : data.originalPrice.toString(),
      cost: data.isRemoved ? "" : data.cost.toString(),
      isRemoved: data.isRemoved,
      onHand: data.onHandInventory.toString(),
      attributeMappings:
        data?.attributeMappings.map((d) => {
          return ProductAttributeWithOption.deserializeAPIModel(d);
        }) || [],
    };
    return new ProductAttributeMappingsInput(mappedData);
  }
}

export class ProductAttributeWithOption extends CoreModel {
  attributeId: string;
  optionId: string;
  attributeName: string;
  optionName: string;
  groupId: string;
  name: string;
  type: AttributeTypes | "";
  valueType: AttributeValueTypes;
  color?: ColorModel;
  errors: InputErrorModel<ProductAttributeWithOption>[];
  //ONLY FOR EDITABLE MODE IN VARIANT
  isShowColorPicker: boolean;
  referenceId: string;
  medias: MediaModel[];

  constructor(data?: Partial<ProductAttributeWithOption>) {
    super();
    this.attributeId = (data && data.attributeId) || "";
    this.optionId = (data && data.optionId) || "";
    this.attributeName = data?.attributeName || "";
    this.optionName = data?.optionName || "";
    this.name = data?.name || "";
    this.groupId = data?.groupId || "";
    this.type = data?.type || AttributeTypes.Custom;
    this.valueType = data?.valueType || AttributeValueTypes.Text;
    this.color = data?.color;
    this.errors = data?.errors || [];
    this.isShowColorPicker = data?.isShowColorPicker === true;
    this.referenceId = data?.referenceId || "";
    this.medias = data?.medias?.map((m) => new MediaModel(m)) || [];
    autorun(() => {
      this.validate(true);
    });
  }

  createAPIModel(): IAPIProductAttributeOptionsMappingModel {
    const request = {
      attributeId: this.attributeId,
      optionId: this.optionId,
      attributeName: this.attributeName,
      optionName: this.optionName,
      type: this.type,
      valueType: this.valueType,
      color: this.color ? ColorModel.createAPIModel(this.color) : undefined,
      medias: this.medias,
    };
    return request as IAPIProductAttributeOptionsMappingModel;
  }

  static deserializeAPIModel(
    data: IAPIProductAttributeOptionsMappingModel
  ): ProductAttributeWithOption {
    const mappedData: Partial<ProductAttributeWithOption> = {
      attributeId: data.attributeId,
      optionId: data.optionId,
      attributeName: data.attributeName,
      optionName: data.optionName,
      color: data.color
        ? ColorModel.deserializeAPIModel(data.color)
        : undefined,
      type: data.type,
      valueType: data.valueType,
      medias: data.medias || [],
    };

    return new ProductAttributeWithOption(mappedData);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductAttributeWithOption>[] = [];
    this.validateTextField(this.optionName, "optionName", newErrors);
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return newErrors.length === 0;
  }
}

// export class ProductOptionsPageModel {
//     @observable items: RelatedItemsCategoriesModel[];
//     @observable accountCredits: string;
//     @observable categories: ProductOptionsCategoriesModel[];
//     @observable showSelectItems: boolean;
//     @observable showAccountCredits: boolean;
//     @observable showCategories: boolean;
//     errors: any[];
//     constructor(data?: Partial<ProductOptionsPageModel>) {
//         this.items = data?.items || [];
//         this.accountCredits = data?.accountCredits || '';
//         this.categories = data?.categories || [];
//         this.showSelectItems = data?.showSelectItems === true;
//         this.showAccountCredits = data?.showAccountCredits === true;
//         this.showCategories = data?.showCategories === true;
//         this.errors = data?.errors || [];

//         autorun(() => {
//             this.validate(true);
//         });
//     }

//     validate(isAutoRun = false): any[] {
//         const newErrors: any[] = [];

//         if (this.showAccountCredits) {
//             if (!this.accountCredits || !DecimalRegex.test(this.accountCredits)) {
//                 {
//                     newErrors.push({
//                         field: nameof<ProductOptionsPageModel>('accountCredits'),
//                     });
//                 }
//             }
//         }
//         if (this.showCategories) {
//             if (!this.categories || this.categories.length === 0) {
//                 {
//                     newErrors.push({
//                         field: nameof<ProductOptionsPageModel>('categories'),
//                     });
//                 }
//             }
//         }
//         //DO NOT ADD ANY NEW ERROR
//         if (isAutoRun) {
//             this.errors = getNewErrors(newErrors, this.errors);
//         } else {
//             this.errors = newErrors;
//         }
//         return this.errors;
//     }

//     static deserializeAPIModel(data: IAPIOptionsSelectedDataRequest): ProductOptionsPageModel {
//         const mappedData: Partial<ProductOptionsPageModel> = {
//             accountCredits: data.accountCredits.toString(),
//             showSelectItems: data.category === ItemOptionTypes.Item,
//             showCategories: data.category === ItemOptionTypes.Category,
//             showAccountCredits: data.category === ItemOptionTypes.IssueCredits,
//             categories:
//                 data.items?.map((c) => {
//                     return ProductOptionsCategoriesModel.deserializeAPIModel(c);
//                 }) || [],
//         };
//         return new ProductOptionsPageModel(mappedData);
//     }
// }

// export class ProductOptionsCategoriesModel {
//     id: string;
//     name: string;
//     isNew: boolean;
//     constructor(data?: Partial<ProductOptionsCategoriesModel>) {
//         this.id = data?.id || '';
//         this.name = data?.name || '';
//         this.isNew = data?.isNew === true;
//     }

//     static deserializeAPIModel(data: IAPISelectedItemsRequest): ProductOptionsCategoriesModel {
//         const mappedData: Partial<ProductOptionsCategoriesModel> = {
//             id: data.id,
//             name: data.name,
//         };
//         return new ProductOptionsCategoriesModel(mappedData);
//     }
// }
